import React from "react";
import { Table, Tag, Button, Space, Popconfirm, message, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { expensesService } from "../../services/api.service";
import moment from "moment";
import logger from "../../utils/logger";

const ExpensesTable = ({ expenses, loading, onEdit, onView, onRefresh }) => {
  // دالة حذف المصروف
  const handleDelete = async (id) => {
    try {
      await expensesService.deleteExpense(id);
      message.success("تم حذف المصروف بنجاح");
      onRefresh();
    } catch (error) {
      logger.error("Error deleting expense:", error);
      message.error(error.message || "فشل في حذف المصروف");
    }
  };

  // أعمدة الجدول
  const columns = [
    {
      title: "التاريخ",
      dataIndex: "expense_date",
      key: "expense_date",
      width: 120,
      render: (date) => moment(date).format("YYYY-MM-DD"),
      sorter: (a, b) => new Date(a.expense_date) - new Date(b.expense_date),
    },
    {
      title: "نوع المصروف",
      dataIndex: "expense_type",
      key: "expense_type",
      width: 150,
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "المبلغ",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount) => (
        <span style={{ fontWeight: "bold" }}>
          {Number(amount).toLocaleString()} د.ع
        </span>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "الوصف",
      dataIndex: "description",
      key: "description",
      width: 250,
      render: (description) => (
        <div className="whitespace-normal break-words">
          {description || '-'}
        </div>
      ),
    },
    {
      title: "رقم الإيصال",
      dataIndex: "receipt_number",
      key: "receipt_number",
      width: 120,
    },
    {
      title: "الحالة",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        let color = "default";
        if (status === "approved") color = "green";
        else if (status === "rejected") color = "red";
        else if (status === "pending") color = "orange";

        let text = "غير محدد";
        if (status === "approved") text = "معتمد";
        else if (status === "rejected") text = "مرفوض";
        else if (status === "pending") text = "قيد الانتظار";

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "الإجراءات",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="عرض التفاصيل">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onView && onView(record)}
            />
          </Tooltip>
          <Tooltip title="تعديل">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => onEdit && onEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="هل أنت متأكد من حذف هذا المصروف؟"
            description="لا يمكن التراجع عن هذه العملية"
            onConfirm={() => handleDelete(record.id)}
            okText="نعم"
            cancelText="لا"
          >
            <Tooltip title="حذف">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // حساب إجمالي المصاريف
  const totalExpenses = expenses.reduce(
    (total, expense) => total + Number(expense.amount || 0),
    0
  );

  return (
    <div>
      <Table
        columns={columns}
        dataSource={expenses}
        loading={loading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} من أصل ${total} مصاريف`,
        }}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}>
              <strong>الإجمالي</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2}>
              <strong>{totalExpenses.toLocaleString()} د.ع</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} colSpan={4}></Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default ExpensesTable;
